import { defineStore } from 'pinia';
import { ApiError, client } from '@/api';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ProviderDiscountGetListRequest } from '@/api/request/providers-discounts/provider-discount-get-list.request';
import { GetGamesRequest } from '@/api/request/games/get-games.request';
import {
  Game,
  GameResponse,
  PaginatedProviderDiscountResponse,
  ProviderDiscountCreate,
  ProviderDiscountResponse,
  ProviderDiscountState,
  ProviderDiscountUpdate,
  UpdateProviderDiscountCommentRequest,
} from '@/open-api/models';
import { debounce } from 'es-toolkit';
import { watch } from 'vue';

export type ProvidersDiscountsState = {
  isWatching: boolean;
  loading: boolean;
  gamesLoading: boolean;
  error: string | null;
  gamesError: string | null;
  currentProviderDiscount: ProviderDiscountResponse | null;
  isCreateOpened: boolean;
  isUpdateOpened: boolean;
  isDeleteOpened: boolean;

  search: string | null;
  games: (string | number)[] | null;
  aggregatorId: number | string | null;
  providerId: number | string | null;
  fee: number | string | null;
  state: ProviderDiscountState | null;

  discounts: ProviderDiscountResponse[];

  pagination: {
    page: number;
    perPage: number;
    total: number;
    pageCount: number;
  };
};

const DEFAULT_PAGINATION = {
  page: 1,
  perPage: 15,
  total: 0,
  pageCount: 0,
};

export const useProvidersDiscounts = defineStore('providersDiscounts', {
  state: (): ProvidersDiscountsState => ({
    isWatching: false,
    loading: false,
    gamesLoading: false,
    error: null,
    gamesError: null,
    currentProviderDiscount: null,
    isCreateOpened: false,
    isUpdateOpened: false,
    isDeleteOpened: false,

    search: null,
    games: null,
    aggregatorId: null,
    providerId: null,
    fee: null,
    state: null,

    discounts: [],

    pagination: { ...DEFAULT_PAGINATION },
  }),

  actions: {
    init() {
      const debounceFetch = debounce(() => {
        if (this.isWatching) {
          this.refresh();
        }
      }, 300);

      watch(
        () => [
          this.search,
          this.games,
          this.aggregatorId,
          this.providerId,
          this.fee,
          this.state,
        ],
        () => debounceFetch(),
        { immediate: true },
      );
    },

    reset() {
      this.$reset();
    },

    async refresh() {
      await this.getProvidersDiscounts({});
    },

    setPagination({ page, limit }: { page: number; limit: number }): void {
      this.pagination = {
        ...this.pagination,
        page,
        perPage: limit,
      };

      this.refresh();
    },

    setLoading(value: boolean) {
      this.loading = value;
    },
    setGamesLoading(value: boolean) {
      this.gamesLoading = value;
    },
    setError(value: string | null) {
      this.error = value;
    },
    setGamesError(value: string | null) {
      this.gamesError = value;
    },
    setCurrentDiscount(item: ProviderDiscountResponse | null) {
      this.currentProviderDiscount = item;
    },

    openCreate() {
      this.isCreateOpened = true;
    },

    openUpdate(item: ProviderDiscountResponse) {
      this.setCurrentDiscount(item);
      this.isUpdateOpened = true;
    },

    openDelete(item: ProviderDiscountResponse) {
      this.setCurrentDiscount(item);
      this.isDeleteOpened = true;
    },

    closeCreate() {
      this.isCreateOpened = false;
    },
    closeUpdate() {
      this.isUpdateOpened = false;
    },
    closeDelete() {
      this.isDeleteOpened = false;
    },

    async getProvidersDiscounts(
      dto: ProviderDiscountGetListRequest = {},
    ): Promise<PaginatedProviderDiscountResponse> {
      this.setError(null);
      this.setLoading(true);

      let data: PaginatedProviderDiscountResponse = {
        items: [],
        total: 0,
        page: 1,
        perPage: 10,
        pageCount: 1,
      };

      try {
        data = await client.providersDiscountApi.getProvidersDiscounts({
          ...dto,

          fee: (this.fee as number) || undefined,
          aggregatorId: (this.aggregatorId as number) || undefined,
          providerId: (this.providerId as number) || undefined,
          state: (this.state as ProviderDiscountState) || undefined,
          games:
            Array.isArray(this.games) && this.games.length > 0
              ? (this.games as number[])
              : undefined,

          message:
            this.search && this.search.length >= 3 ? this.search : undefined,

          pageNumber: this.pagination.page,
          pageSize: this.pagination.perPage,
        });

        const { items, ...pagination } = data;

        this.discounts = items ?? [];
        this.pagination = pagination;
      } catch (err) {
        let message = 'Error during getting list of Providers Discounts';

        if ((err as ApiError)?.body?.message) {
          message = (err as ApiError).body.message;
        }

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return data;
    },

    async getProvidersDiscountsGames(
      dto: GetGamesRequest = {},
    ): Promise<GameResponse> {
      this.setGamesError(null);
      this.setGamesLoading(true);

      let data: GameResponse = {
        items: [],
        total: 0,
      };

      try {
        data = await client.providersDiscountApi.getProvidersDiscountsGames(
          dto,
        );
      } catch (err) {
        let message = 'Error during getting list of Providers Discounts Games';

        if ((err as ApiError)?.body?.message) {
          message = (err as ApiError).body.message;
        }

        ElMessage.error(message);

        this.setGamesError(message);
      } finally {
        this.setGamesLoading(false);
      }

      return data;
    },

    async getDictGame(id: string): Promise<Game | null> {
      try {
        return await client.providersDiscountApi.getDictGame(id);
      } catch {
        return null;
      }
    },

    async createProviderDiscount(
      dto: ProviderDiscountCreate,
    ): Promise<ProviderDiscountResponse | null> {
      this.setError(null);
      this.setLoading(true);

      try {
        return await client.providersDiscountApi.createProviderDiscount(dto);
      } catch (err) {
        const message = (err as ApiError).message ?? 'Error during creating Provider Discount. Check if the same Fee is already existed.';

        ElMessage.error(message);
      } finally {
        this.setLoading(false);
      }

      return null;
    },

    async updateProviderDiscount(
      id: number,
      dto: ProviderDiscountUpdate,
    ): Promise<ProviderDiscountResponse | null> {
      this.setError(null);
      this.setLoading(true);

      try {
        return await client.providersDiscountApi.updateProviderDiscount(
          id,
          dto,
        );
      } catch (err) {
        const message = 'Error during updating Provider Discount';

        ElMessage.error(message);
      } finally {
        this.setLoading(false);
      }

      return null;
    },

    async updateProviderDiscountComment(
      id: number,
      dto: UpdateProviderDiscountCommentRequest,
    ): Promise<ProviderDiscountResponse | null> {
      this.setError(null);
      this.setLoading(true);

      try {
        return await client.providersDiscountApi.updateProviderDiscountComment(
          id,
          dto,
        );
      } catch (err) {
        const message = 'Error during updating Provider Discount comment';

        ElMessage.error(message);
      } finally {
        this.setLoading(false);
      }

      return null;
    },

    async deleteProviderDiscount(id: number): Promise<void> {
      try {
        await ElMessageBox.confirm('Please confirm deleting Provider Discount');
      } catch (err) {
        return;
      }

      this.setError(null);
      this.setLoading(true);

      try {
        await client.providersDiscountApi.deleteProviderDiscount(id);
      } catch (err) {
        const message = 'Error during removing Provider Discount';

        ElMessage.error(message);
      } finally {
        this.setLoading(false);
      }
    },
  },
});
